import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight } from "lucide-react";

export default function LoginPage() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(identifier, password);
      toast.success("Login successful");
      if (!user.nda_accepted) {
        navigate("/nda");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16 bg-white">
        <div className="w-full max-w-md animate-slide-in">
          {/* Logo */}
          <div className="mb-12">
            <img 
              src="https://rimcore.in/images/RIMCOREWHITEBACKREMOVED.png" 
              // src="https://customer-assets.emergentagent.com/job_reinforcement-hub/artifacts/rc0x4325_RimCorePreCut.png" 
              alt="RimCore Precut" 
              className="h-16 object-contain"
              data-testid="logo"
            />
          </div>

          {/* Title */}
          <div className="mb-8">
            <h1 className="font-heading text-4xl font-bold text-slate-900 uppercase tracking-tight" data-testid="login-title">
              Sign In
            </h1>
            <p className="mt-2 text-slate-500">
              Access your industrial dashboard
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="identifier" className="text-xs uppercase tracking-wider text-slate-600">
                Customer Code / Email
              </Label>
              <Input
                id="identifier"
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="h-12 rounded-none border-slate-300 bg-slate-50 focus:ring-1 focus:ring-slate-900 focus:border-slate-900"
                placeholder="CUST-001 or admin@company.com"
                required
                data-testid="identifier-input"
              />
              <p className="text-xs text-slate-400">
                Customers: Enter your Customer Code | Staff: Enter your Email
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-600">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 rounded-none border-slate-300 bg-slate-50 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 pr-12"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-none bg-slate-900 text-white font-heading uppercase tracking-wider hover:bg-slate-800 btn-industrial"
              data-testid="submit-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent animate-spin"></div>
                  Processing...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Sign In
                  <ArrowRight size={18} />
                </span>
              )}
            </Button>
          </form>

          {/* Note */}
          <div className="mt-8 pt-6 border-t border-slate-200 text-center">
            <p className="text-slate-500 text-sm">
              Contact your administrator if you need access credentials.
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Image */}
      <div 
        className="hidden lg:flex w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1769250707038-7cd04e685abe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzdGVlbCUyMGZhY3RvcnklMjBpbnRlcmlvciUyMGluZHVzdHJpYWwlMjBhYnN0cmFjdHxlbnwwfHx8fDE3NzI1NDc2Mjh8MA&ixlib=rb-4.1.0&q=85')" }}
      >
        <div className="absolute inset-0 bg-slate-900/80"></div>
        <div className="relative z-10 flex flex-col justify-end p-16 text-white">
          <div className="border-l-4 border-orange-500 pl-6">
            <h2 className="font-heading text-5xl font-bold uppercase tracking-tight mb-4">
              Precision Delivered.
            </h2>
            <h2 className="font-heading text-5xl font-bold uppercase tracking-tight text-orange-500">
              Process Simplified.
            </h2>
          </div>
          <p className="mt-8 text-slate-300 text-lg max-w-md">
            We do not sell reinforcement sheets. We deliver ready-to-use precision-cut reinforcement components.
          </p>
        </div>
      </div>
    </div>
  );
}
